import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../api";
import { Seo } from "../components/Seo";
import { RichText } from "../components/RichText";
import { ServiceCTA } from "../components/LeadCta";

export default function Blog() {
  const { lang } = useApp();
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-10" data-testid="blog-page">
      <Seo title="Blog — Gujarat Property Guides" description="Guides on 7/12, 8-A, Ferfar, Index-2, Certified Copy, Stamp Duty, Registration, GARVI 2.0, Rent Agreement and E-Stamping in Gujarat." />
      <h1 className="font-display font-bold text-3xl md:text-4xl">DocGalaxy Blog</h1>
      <p className="text-muted-foreground mt-2 mb-8">Gujarat property documents, explained simply.</p>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {posts.filter((p) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(p.slug || "")).map((p) => (
          <a key={p.id} href={`/blog/${p.slug}`} className="bg-white border border-border rounded-xl p-6 card-hover block" data-testid={`blog-card-${p.slug}`}>
            <span className="text-[10px] font-bold uppercase tracking-wide text-secondary">{p.category}</span>
            <h2 className="font-display font-semibold text-lg mt-2">{lang === "gu" && p.title_gu ? p.title_gu : p.title_en}</h2>
            <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{p.meta_description}</p>
            <span className="text-primary text-sm font-semibold mt-3 inline-block">Read more →</span>
          </a>
        ))}
      </div>
    </div>
  );
}

const CTA_BY_CATEGORY = {
  "GARVI 2.0": "Garvi 2.0 Assistance",
  "E-Stamping": "E-Stamping",
  "Rent Agreement": "Rent Agreement",
  "Index-2": "Index-2 / Certified Copy",
  "7/12": "7/12 / 8-A / Property Card",
  "Property Verification": "Property Verification",
};

function serviceFor(post) {
  if (CTA_BY_CATEGORY[post.category]) return CTA_BY_CATEGORY[post.category];
  const s = `${post.slug} ${post.category}`.toLowerCase();
  if (s.includes("garvi")) return "Garvi 2.0 Assistance";
  if (s.includes("stamp")) return "E-Stamping";
  if (s.includes("rent")) return "Rent Agreement";
  if (s.includes("index") || s.includes("certified")) return "Index-2 / Certified Copy";
  if (s.includes("7-12") || s.includes("property-card") || s.includes("8a")) return "7/12 / 8-A / Property Card";
  return "Other";
}

export function BlogPost() {
  const { slug } = useParams();
  const { lang } = useApp();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setData(null); setMissing(false);
    api.get(`/blog/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="blog-not-found">
        <Seo title="Article not found" noindex />
        <h1 className="font-display font-bold text-2xl">Article not found</h1>
        <a href="/blog" className="inline-block mt-6 px-6 py-3 rounded-lg bg-primary text-white font-semibold text-sm">All articles</a>
      </div>
    );
  }
  if (!data) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">Loading...</div>;
  const { post, related } = data;
  const guFirst = post.primary_lang === "gu" ? true : lang === "gu";
  const title = guFirst && post.title_gu ? post.title_gu : post.title_en;
  const body = guFirst && post.body_gu ? post.body_gu : post.body_en;
  const altBody = guFirst ? post.body_en : post.body_gu;
  const faqs = post.faqs || [];
  const origin = window.location.origin;
  const url = `${origin}/blog/${post.slug}`;
  const service = serviceFor(post);
  const dateP = post.created_at?.slice(0, 10);
  const dateM = (post.updated_at || post.created_at)?.slice(0, 10);

  const schema = [
    {
      "@context": "https://schema.org", "@type": "Article", headline: post.title_en, alternativeHeadline: post.title_gu || undefined,
      description: post.meta_description, keywords: post.focus_keyword, inLanguage: guFirst ? "gu" : "en",
      datePublished: dateP, dateModified: dateM, mainEntityOfPage: { "@type": "WebPage", "@id": url }, url,
      author: { "@type": "Organization", name: "DocGalaxy Gujarat", url: origin },
      publisher: { "@type": "Organization", name: "DocGalaxy Gujarat", url: origin },
      articleSection: post.category,
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
        { "@type": "ListItem", position: 3, name: post.title_en, item: url },
      ],
    },
  ];
  if (faqs.length) {
    schema.push({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question", name: guFirst ? f.q_gu || f.q_en : f.q_en || f.q_gu,
        acceptedAnswer: { "@type": "Answer", text: guFirst ? f.a_gu || f.a_en : f.a_en || f.a_gu },
      })),
    });
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="blog-post-page">
      <Seo title={title} description={post.meta_description} type="article" canonicalPath={`/blog/${post.slug}`} />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <nav className="text-xs text-muted-foreground mb-4" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <a href="/" className="hover:text-primary">Home</a> / <a href="/blog" className="hover:text-primary">Blog</a> / <span>{post.category}</span>
      </nav>
      <h1 className="font-display font-bold text-3xl md:text-4xl leading-tight">{title}</h1>
      {guFirst && post.title_gu && post.title_en !== title && <p className="text-muted-foreground mt-2">{post.title_en}</p>}
      {dateM && <p className="text-xs text-muted-foreground mt-2" data-testid="post-date">{lang === "gu" ? "અપડેટ" : "Updated"}: {dateM}</p>}

      <RichText text={body} className="mt-6" />

      {faqs.length > 0 && (
        <section className="mt-10" data-testid="post-faqs">
          <h2 className="font-display font-bold text-xl md:text-2xl">{guFirst ? "વારંવાર પૂછાતા પ્રશ્નો (FAQ)" : "Frequently Asked Questions"}</h2>
          <div className="mt-4 space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="bg-white border border-border rounded-xl p-4 group" data-testid={`post-faq-${i}`}>
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center gap-3">
                  <span>{guFirst ? f.q_gu || f.q_en : f.q_en || f.q_gu}</span>
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{guFirst ? f.a_gu || f.a_en : f.a_en || f.a_gu}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <ServiceCTA service={service} />

      {altBody && (
        <details className="mt-10 border-t border-border pt-6" data-testid="post-alt-language">
          <summary className="font-display font-semibold text-lg cursor-pointer">{guFirst ? "English version" : "ગુજરાતી આવૃત્તિ"}</summary>
          <RichText text={altBody} className="mt-4 text-sm" />
        </details>
      )}

      {related?.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display font-semibold text-lg mb-3">{lang === "gu" ? "સંબંધિત લેખો" : "Related articles"}</h3>
          <div className="space-y-2">
            {related.map((r) => (
              <a key={r.id} href={`/blog/${r.slug}`} className="block text-primary hover:underline text-sm font-medium" data-testid={`related-${r.slug}`}>
                {lang === "gu" && r.title_gu ? r.title_gu : r.title_en}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
